/**
 * useRiskApplications (page-level hook) — Phase 7 rewrite
 *
 * Bridges the unified riskApplicationService with the RiskApplicationPage's
 * existing UX: available/applied split, bulk apply/remove, edit, alerts.
 */

import { useState, useCallback } from 'react';
import apiClient from '../../../services/apiClient';
import { riskApplicationService } from '../../../services/riskApplicationService';
import { occupationalRiskService } from '../../../services/occupationalRiskService';
import { OccupationalRiskStatus } from '../../../types/occupationalRisk';
import { getApiErrorMessage } from '../../../types/common';
import type {
  RiskApplicationScope,
  RiskApplication,
  RiskApplicationData,
  ExposureLevel,
} from '../../../types/riskApplication';

export type { RiskApplicationData };
import {
  ApplicationLevel,
} from '../../../types/riskApplication';

// ============================================================================
// ADAPTED TYPES (kept for RiskApplicationPage compatibility)
// ============================================================================

export interface RiskWithApplication {
  id: number;
  riskId: number;
  riskName: string;
  riskFactorName: string;
  hazard: string;
  exposureLevel: ExposureLevel;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
  isActive: boolean;
  isCritical: boolean;
  exposureDays: number;
  applicationLevel: ApplicationLevel;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableRisk {
  id: number;
  risk: string;
  riskFactorName: string;
  hazard: string;
  riskType?: string | null;
}

export interface CriticalExposureAlerts {
  critical: Array<{
    applicationId: number;
    riskId: number;
    riskName: string;
    targetId: number;
    targetType: ApplicationLevel;
    targetName: string;
    exposureLevel: ExposureLevel;
    exposureDays: number;
    startDate: string;
  }>;
  summary: {
    totalCritical: number;
    veryHighCount: number;
    highCount: number;
  };
}

// ============================================================================
// HELPERS
// ============================================================================

const scopeToLevel: Record<RiskApplicationScope, ApplicationLevel> = {
  company: ApplicationLevel.COMPANY,
  unit: ApplicationLevel.UNIT,
  department: ApplicationLevel.DEPARTMENT,
  occupation: ApplicationLevel.OCCUPATION,
  employee: ApplicationLevel.EMPLOYEE,
};

function toRiskWithApplication(app: RiskApplication): RiskWithApplication {
  const startMs = app.startDate ? new Date(app.startDate).getTime() : Date.now();
  const endMs = app.endDate ? new Date(app.endDate).getTime() : null;
  const nowMs = Date.now();
  const isActive = endMs === null || endMs >= nowMs;
  const exposureDays = Math.max(0, Math.floor((nowMs - startMs) / (1000 * 60 * 60 * 24)));
  const isCritical = app.exposureLevel === 'HIGH' || app.exposureLevel === 'VERY_HIGH';

  return {
    id: app.id,
    riskId: app.riskId,
    riskName: app.risk?.riskFactorName ?? app.risk?.risk ?? `Risco ${app.riskId}`,
    riskFactorName: app.risk?.riskFactorName ?? '',
    hazard: app.risk?.hazard ?? '',
    exposureLevel: app.exposureLevel,
    startDate: app.startDate,
    endDate: app.endDate ?? null,
    observations: app.observations ?? null,
    isActive,
    isCritical,
    exposureDays,
    applicationLevel: scopeToLevel[app.scope],
    createdAt: app.createdAt,
    updatedAt: app.updatedAt,
  };
}

/**
 * E4 (#606, D9): as exposições Alto e Muito alto ainda vigentes da empresa, em qualquer nível
 * dela — o mesmo formato dos alertas de fontes geradoras.
 */
export async function buscarAlertasDeRiscos(companyId: number): Promise<CriticalExposureAlerts> {
  const resposta = await apiClient.get<CriticalExposureAlerts>(`/risk-applications/company/${companyId}/alerts`);
  return resposta.data ?? (resposta as unknown as CriticalExposureAlerts);
}

// ============================================================================
// HOOK
// ============================================================================

interface UseRiskApplicationsState {
  applied: RiskWithApplication[];
  available: AvailableRisk[];
  alerts: CriticalExposureAlerts | null;
  loading: boolean;
  loadingAlerts: boolean;
  error: string | null;
  alertsError: string | null;
}

export interface BulkApplyResult {
  created: number;
  duplicates: number;
}

interface UseRiskApplicationsReturn extends UseRiskApplicationsState {
  loadRisks: (level: ApplicationLevel, targetId: number) => Promise<void>;
  loadAlerts: (companyId: number) => Promise<void>;
  applyRisk: (
    level: ApplicationLevel,
    targetId: number,
    riskId: number,
    data?: RiskApplicationData
  ) => Promise<boolean>;
  applyMultipleRisks: (
    level: ApplicationLevel,
    targetId: number,
    riskIds: number[],
    data?: RiskApplicationData
  ) => Promise<BulkApplyResult | false>;
  removeRisk: (
    level: ApplicationLevel,
    targetId: number,
    riskId: number
  ) => Promise<boolean>;
  removeMultipleRisks: (
    level: ApplicationLevel,
    targetId: number,
    riskIds: number[]
  ) => Promise<boolean>;
  updateApplication: (
    level: ApplicationLevel,
    targetId: number,
    riskId: number,
    data: RiskApplicationData
  ) => Promise<boolean>;
  clearError: () => void;
  clearAlertsError: () => void;
}

const levelToScope: Record<ApplicationLevel, RiskApplicationScope> = {
  [ApplicationLevel.COMPANY]: 'company',
  [ApplicationLevel.UNIT]: 'unit',
  [ApplicationLevel.DEPARTMENT]: 'department',
  [ApplicationLevel.OCCUPATION]: 'occupation',
  [ApplicationLevel.EMPLOYEE]: 'employee',
};

export function useRiskApplications(): UseRiskApplicationsReturn {
  const [state, setState] = useState<UseRiskApplicationsState>({
    applied: [],
    available: [],
    alerts: null,
    loading: false,
    loadingAlerts: false,
    error: null,
    alertsError: null,
  });

  /**
   * Load risks for a target — lists applications and derives applied/available split.
   * Also fetches ALL active occupational risks so "Riscos Disponíveis" is populated.
   */
  const loadRisks = useCallback(
    async (level: ApplicationLevel, targetId: number): Promise<void> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        const [apps, allRisksResponse] = await Promise.all([
          riskApplicationService.list(scope, targetId),
          occupationalRiskService.getOccupationalRisks({
            status: OccupationalRiskStatus.ACTIVE,
            limit: 2000,
          }),
        ]);

        const appliedRiskIds = new Set(apps.map((a) => a.riskId));

        const available: AvailableRisk[] = allRisksResponse.risks
          .filter((r) => !appliedRiskIds.has(r.id))
          .map((r) => ({
            id: r.id,
            risk: r.risk,
            riskFactorName: r.riskFactorName,
            hazard: r.hazard,
            riskType: r.riskType ?? null,
          }));

        setState((prev) => ({
          ...prev,
          applied: apps.map(toRiskWithApplication),
          available,
          loading: false,
        }));
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao carregar riscos'),
        }));
      }
    },
    []
  );

  const loadAlerts = useCallback(async (companyId: number): Promise<void> => {
    setState((prev) => ({ ...prev, loadingAlerts: true, alertsError: null }));
    try {
      const alerts = await buscarAlertasDeRiscos(companyId);
      setState((prev) => ({ ...prev, alerts, loadingAlerts: false }));
    } catch (err: unknown) {
      setState((prev) => ({
        ...prev,
        loadingAlerts: false,
        alertsError: getApiErrorMessage(err, 'Erro ao carregar alertas críticos'),
      }));
    }
  }, []);

  const applyRisk = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      riskId: number,
      data?: RiskApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        await riskApplicationService.apply({
          riskId,
          scope,
          scopeId: targetId,
          exposureLevel: data?.exposureLevel,
          startDate: data?.startDate ?? new Date().toISOString().split('T')[0],
          endDate: data?.endDate,
          observations: data?.observations,
        });
        const apps = await riskApplicationService.list(scope, targetId);
        setState((prev) => ({
          ...prev,
          applied: apps.map(toRiskWithApplication),
          loading: false,
        }));
        return true;
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao aplicar risco'),
        }));
        return false;
      }
    },
    []
  );

  const applyMultipleRisks = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      riskIds: number[],
      data?: RiskApplicationData
    ): Promise<BulkApplyResult | false> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        const bulkResponse = await riskApplicationService.bulkApply({
          riskIds,
          scope,
          scopeId: targetId,
          exposureLevel: data?.exposureLevel,
          startDate: data?.startDate ?? new Date().toISOString().split('T')[0],
          endDate: data?.endDate,
          observations: data?.observations,
        });
        const [apps, allRisksResponse] = await Promise.all([
          riskApplicationService.list(scope, targetId),
          occupationalRiskService.getOccupationalRisks({
            status: OccupationalRiskStatus.ACTIVE,
            limit: 2000,
          }),
        ]);
        const appliedRiskIds = new Set(apps.map((a) => a.riskId));
        const available: AvailableRisk[] = allRisksResponse.risks
          .filter((r) => !appliedRiskIds.has(r.id))
          .map((r) => ({
            id: r.id,
            risk: r.risk,
            riskFactorName: r.riskFactorName,
            hazard: r.hazard,
            riskType: r.riskType ?? null,
          }));
        setState((prev) => ({
          ...prev,
          applied: apps.map(toRiskWithApplication),
          available,
          loading: false,
        }));
        return {
          created: bulkResponse.created.length,
          duplicates: bulkResponse.duplicates.length,
        };
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao aplicar riscos'),
        }));
        return false;
      }
    },
    []
  );

  const removeRisk = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      riskId: number
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        // Find the application by riskId
        const apps = await riskApplicationService.list(scope, targetId);
        const app = apps.find((a) => a.riskId === riskId);
        if (app) await riskApplicationService.remove(app.id);
        const updated = await riskApplicationService.list(scope, targetId);
        setState((prev) => ({
          ...prev,
          applied: updated.map(toRiskWithApplication),
          loading: false,
        }));
        return true;
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao remover risco'),
        }));
        return false;
      }
    },
    []
  );

  const removeMultipleRisks = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      riskIds: number[]
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        const apps = await riskApplicationService.list(scope, targetId);
        const toRemove = apps.filter((a) => riskIds.includes(a.riskId));
        await Promise.all(toRemove.map((a) => riskApplicationService.remove(a.id)));
        const [updated, allRisksResponse] = await Promise.all([
          riskApplicationService.list(scope, targetId),
          occupationalRiskService.getOccupationalRisks({
            status: OccupationalRiskStatus.ACTIVE,
            limit: 2000,
          }),
        ]);
        const appliedRiskIds = new Set(updated.map((a) => a.riskId));
        const available: AvailableRisk[] = allRisksResponse.risks
          .filter((r) => !appliedRiskIds.has(r.id))
          .map((r) => ({
            id: r.id,
            risk: r.risk,
            riskFactorName: r.riskFactorName,
            hazard: r.hazard,
            riskType: r.riskType ?? null,
          }));
        setState((prev) => ({
          ...prev,
          applied: updated.map(toRiskWithApplication),
          available,
          loading: false,
        }));
        return true;
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao remover riscos'),
        }));
        return false;
      }
    },
    []
  );

  const updateApplication = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      riskId: number,
      data: RiskApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const scope = levelToScope[level];
      try {
        const apps = await riskApplicationService.list(scope, targetId);
        const app = apps.find((a) => a.riskId === riskId);
        if (!app) throw new Error('Aplicação não encontrada');
        await riskApplicationService.update(app.id, {
          exposureLevel: data.exposureLevel,
          startDate: data.startDate ?? undefined,
          endDate: data.endDate,
          observations: data.observations,
        });
        const updated = await riskApplicationService.list(scope, targetId);
        setState((prev) => ({
          ...prev,
          applied: updated.map(toRiskWithApplication),
          loading: false,
        }));
        return true;
      } catch (err: unknown) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getApiErrorMessage(err, 'Erro ao atualizar aplicação'),
        }));
        return false;
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const clearAlertsError = useCallback(() => {
    setState((prev) => ({ ...prev, alertsError: null }));
  }, []);

  return {
    ...state,
    loadRisks,
    loadAlerts,
    applyRisk,
    applyMultipleRisks,
    removeRisk,
    removeMultipleRisks,
    updateApplication,
    clearError,
    clearAlertsError,
  };
}

export default useRiskApplications;
